import React, { useEffect, useState } from "react";
import Router from "next/router";

import KLink from "@kikoff/components/src/v1/navigation/KLink";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";

import { StyledButton } from "@component/buttons/buttons";
import PageAlign from "@component/dashboard/offer/page_align";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import { GenericSpinner } from "@component/loading_indicators/loading_indicators";
import styles from "@page/dashboard/store/_views/persona_flow.module.scss";
import { useOverlaysController } from "@src/overlay";
import { track } from "@util/analytics";
import { nativeDispatchEvent } from "@util/mobile";

interface PersonaFlowViewProps extends PageRegistration {}

export default function PersonaFlowView({}: PersonaFlowViewProps) {
  const [personaHref, setPersonaHref] = useState(null);
  const overlays = useOverlaysController();
  const error = useErrorConsumer();

  const redirectUri = encodeURIComponent(
    `${window.location.origin}/dashboard?persona-success`
  );

  useEffect(() => {
    webRPC.Account.getPersonaAddressVerificationLink({})
      .then(
        handleProtoStatus({
          SUCCESS({ personaAddressVerificationUrl }) {
            setPersonaHref(personaAddressVerificationUrl);
          },
          _DEFAULT: handleFailedStatus(
            "Failed to get address verification link"
          ),
        })
      )
      .catch((e) => {
        error.throw(e);
      });
  }, []);

  if (!personaHref) return <GenericSpinner />;

  const onClose = () => {
    track.tap("Persona - Intro Screen Close Button");
  };

  return (
    <PageAlign wrap>
      <div className={styles["persona-view"]}>
        <PageAlign wrap className={styles.close}>
          <KLink
            href="/dashboard"
            onClick={nativeDispatchEvent("close", {
              defaultHandler: onClose,
            })}
          >
            
          </KLink>
        </PageAlign>
        <div className="text:heading-4">Additional Confirmation Needed</div>
        <div className={styles.notice}>
          <div className="text:regular">
            To ensure accurate credit reporting, please provide supporting
            documentation of your address.
          </div>
        </div>

        <a
          onClick={async (e) => {
            const payload = await overlays.push(
              "identity_verification/persona_upload",
              {
                link: personaHref,
              }
            );
            if (!payload) return;

            const { status: personaStatus, error: err } = payload;
            if (err) throw new Error(err);
            else if (personaStatus === "failed") {
              throw new Error("Failed to upload documents.");
            } else {
              const nextStop = () => {
                Router.replace("/dashboard?persona-success");
                Router.events.off("routeChangeComplete", nextStop);
              };
              Router.events.on("routeChangeComplete", nextStop);
            }
          }}
        >
          <StyledButton shape="round" className={styles.button}>
            Confirm with Persona
          </StyledButton>
        </a>
      </div>
    </PageAlign>
  );
}
